import {
  LinkSystem,
  MyBenefitHeader, // eslint-disable-next-line
} from "@benefit-systems/hr-tools-ui";
// eslint-disable-next-line
import "@benefit-systems/shared/fonts/main.css";
import { isWebView, useLegacyWebViewState } from "@benefit-systems/webview";
import {
  changePin,
  toggleLocalAuth,
} from "@benefit-systems/webview/plugins/localAuth";
import { ThemeProvider } from "@mui/material";
import {
  AnchorHTMLAttributes,
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import useLogout from "web/Pages/Customer/Content/Main/Logout/utils/useLogout";

import customTheme from "web/Layout/MainHeader/Desktop/MBHeader/theme/customTheme";
import IntercomTrigger from "web/Layout/MainHeader/Shared/IntercomTrigger";
import MessagesTrigger from "web/Layout/MainHeader/Shared/MessagesTrigger/container";
import __ from "web/Layout/Translations";
import {
  availableLanguages,
  defaultLanguage,
} from "web/Layout/Translations/LanguageWrapper";

import { IMPERSONATE_COOKIE_NAME } from "web/utils/system/cookie/const";
import removeCookie from "web/utils/system/cookie/removeCookie";

import { ombRedirect } from "web/constants/urls";

import { useAppContext } from "web/context/app";
import { useGetCompanyDetailsQuery } from "web/features/company/companyApiSlice";
import {
  useGetCustomerDetailsQuery,
  useUpdateCustomerMutation,
} from "web/features/customer/customerApiSlice";
import { useGetDashboardHeaderDataQuery } from "web/features/dashboard/dashboardApiSlice";
import isEmployeeCardEnabled from "web/features/dashboard/isEmployeeCardEnabled";
import { useGetOrganizationStructureEmployeeMeDataQuery } from "web/features/organizationStructure/organizationStructureApiSlice";
import { flushReduxPersistCache } from "web/store";

import { moduleHeaderPaths } from "../../headerPaths";

interface LinkComponentProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  children?: ReactNode;
}

const MBHeader = () => {
  const [companyLanguages, setCompanyLanguages] = useState<
    typeof availableLanguages
  >([]);
  const { isMobile } = useAppContext();
  const [lang, setLang] = useState(defaultLanguage);
  const { data: customer } = useGetCustomerDetailsQuery();
  const { language: userLanguage } = customer || {};
  const { data } = useGetCompanyDetailsQuery();
  const languages = data?.languages || [];
  const [updateCustomerFn] = useUpdateCustomerMutation();
  const { localAuthType, isLocalAuthEnabled, setLocalAuthStatus } =
    useLegacyWebViewState();
  const [shouldDisplayPoints, setShouldDisplayPoints] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);

  const { data: dashboardMenu } = useGetDashboardHeaderDataQuery();
  const { data: employeeMeData } =
    useGetOrganizationStructureEmployeeMeDataQuery();
  const location = useLocation();
  const { logoutHandler } = useLogout();
  const { ssoRole, impersonated } = customer || {};
  const isAhr = ssoRole === "ahr";

  const moduleMatch = useMemo(
    () => moduleHeaderPaths.find((m) => location.pathname.startsWith(m.path)),
    [location.pathname]
  );

  const isOnForbiddenPath = !!moduleMatch;

  const mobileModuleHeader = useMemo(() => {
    const MODULE_LABEL_MAX_LENGTH = 24;
    const DEFAULT_LABEL_MAX_LENGTH = 18;
    const label = moduleMatch ? __(moduleMatch.label) : __("Kafeteria");
    const maxLength = moduleMatch
      ? MODULE_LABEL_MAX_LENGTH
      : DEFAULT_LABEL_MAX_LENGTH;
    const truncatedLabel =
      label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;

    return {
      label: truncatedLabel,
      link: moduleMatch?.path || "/",
    };
  }, [moduleMatch, lang]);

  useEffect(() => {
    setCompanyLanguages(
      availableLanguages.filter(
        ({ key, code }) => languages?.includes(code) || key === defaultLanguage
      )
    );
  }, [data]);

  useEffect(() => {
    if (userLanguage) {
      setLang(userLanguage);
    }
  }, [userLanguage]);

  const handleLangChange = async (value: string | null) => {
    if (value !== null) {
      await flushReduxPersistCache();
      setLang(value);
      updateCustomerFn({
        language: value,
      });
    }
  };

  const backToOmbAsAhrHandler = () => {
    if (isAhr) {
      removeCookie(IMPERSONATE_COOKIE_NAME);
      window.open(
        `${ombRedirect.redirectAhrToOmb}?logoutUrl=${window.location.origin}/customer/account/logout`,
        "_self"
      );
    }
  };

  const backToOmbAsAdmHandler = () => {
    if (impersonated) {
      removeCookie(IMPERSONATE_COOKIE_NAME);
      window.open(ombRedirect.redirectOperatorToOmb, "_self");
    }
  };

  const handleChangePointsVisibility = () => {
    setShouldDisplayPoints(!shouldDisplayPoints);
  };

  const LinkComponent = useMemo(
    () =>
      forwardRef<HTMLAnchorElement, LinkComponentProps>(
        ({ to, children, ...props }, ref) => {
          if (to.startsWith("http")) {
            return (
              <a {...props} href={to} rel="noreferrer" ref={ref}>
                {children}
              </a>
            );
          }
          return (
            <Link to={to} {...props} ref={ref}>
              {children}
            </Link>
          );
        }
      ),
    []
  );

  return (
    <ThemeProvider theme={customTheme()}>
      <MyBenefitHeader
        ref={headerRef}
        mainMenu={dashboardMenu?.mainMenu || []}
        userMenu={dashboardMenu?.userMenu || []}
        location={location}
        pointsBalance={dashboardMenu?.pointsBank?.balance || 0}
        logo={{
          companyName: dashboardMenu?.logo?.companyName || "",
          src: dashboardMenu?.logo?.src || "",
          url: dashboardMenu?.logo?.url || undefined,
        }}
        employee={{
          firstName: dashboardMenu?.employee?.firstName || " ",
          lastName: dashboardMenu?.employee?.lastName || " ",
          employeeId: "",
          email: dashboardMenu?.employee?.email || "",
          language: dashboardMenu?.employee?.language || "pl",
          position: employeeMeData?.position?.value || "",
          avatar: dashboardMenu?.employeeStructureData?.avatar?.original || "",
        }}
        isEmployeeCardEnabled={isEmployeeCardEnabled(dashboardMenu?.products)}
        onLogout={logoutHandler}
        Link={LinkComponent}
        translate={__}
        isAhr={isAhr}
        dashboardUrl={process.env.MB_DASHBOARD_URL}
        originSystem={LinkSystem.CAFETERIA}
        isUserImpersonated={impersonated || false}
        onAdminRedirect={backToOmbAsAhrHandler}
        onOmbRedirect={backToOmbAsAdmHandler}
        lang={{ value: lang, companyLanguages, availableLanguages: languages }}
        {...(languages && { onLanguageChange: handleLangChange })}
        employeeUrl={`${process.env.MB_DASHBOARD_URL}/os/employees/me`}
        breakpoint={980}
        isMobileApp={isWebView()}
        isLocalAuthEnabled={isLocalAuthEnabled}
        localAuthType={localAuthType}
        onChangePin={changePin}
        toggleLocalAuth={() => {
          toggleLocalAuth(!isLocalAuthEnabled).then((enabled) => {
            setLocalAuthStatus((prevState) => ({
              ...prevState,
              isLocalAuthEnabled: enabled,
            }));
          });
        }}
        shouldPointsBeVisible={shouldDisplayPoints}
        onPointsVisibilityChange={handleChangePointsVisibility}
        shouldDisplayIntercom={isMobile}
        IntercomButton={IntercomTrigger}
        shouldDisplayPointsValue={!isMobile}
        shouldDisplayNotifications={isMobile && !isOnForbiddenPath}
        NotificationsButton={MessagesTrigger}
        mobileModuleHeader={mobileModuleHeader}
      />
    </ThemeProvider>
  );
};

export default MBHeader;
